import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import _ from 'lodash';

import SelectFromWaitlistModal from './SelectFromWaitlistModal';
import { useSelectFromWaitlist } from './hooks';
import { skipReasons, cancel } from './constants';
import selectFromWaitlistMessages from './messages';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import useIntl from '../../../hooks/useIntl';
import useHasPermission from '../../../hooks/useHasPermission';
import { navigateToUrlWithSelectedPropertyId } from '../../../utils/navigation-helpers';
import messages from '../../../containers/ManageAffordableWaitlistV2/messages';
import PermissionGatedActions from '../PermissionGatedActions';
import { WAITLIST_ACTION_SCOPE } from '../constants';
import waitlistActionMessages from '../messages';

// Single endpoint handles both "confirm" (rank 1) and "skip" (any other
// rank) — the only difference is whether reasons/description are included,
// decided here based on the position's rank, not by calling a different
// endpoint.
export const buildSelectFromWaitlistPayload = ({
  affordableWaitlistRow,
  floorPlanId,
  preferredMoveInDate,
  reasons,
  description,
}) => {
  const {
    id: affordableWaitlistPositionId,
    applicationId,
    rank,
  } = affordableWaitlistRow || {};

  const payload = {
    applicationId,
    affordableWaitlistPositionId,
    floorPlanId,
    preferredMoveInDate,
  };

  if (rank !== 1) {
    payload.reasons = reasons ?? [];
    payload.description = description ?? '';
  }

  return payload;
};

const SelectFromWaitlist = ({
  organizationId,
  propertyId,
  open,
  handleClose,
  modalState,
  refetchPositions,
  separateByBedroom,
  waitlistV2Programs,
}) => {
  const { affordableWaitlistId, affordableWaitlistRow } = modalState || {};
  const { rank } = affordableWaitlistRow || {};
  const intl = useIntl();

  const onSuccess = (applicationGuid) => () => {
    handleClose();
    refetchPositions();
    if (applicationGuid) {
      navigateToUrlWithSelectedPropertyId(`/application/${applicationGuid}`);
    }
  };

  const { mutate: selectFromWaitlistMutate, isLoading: isSubmitting } =
    useSelectFromWaitlist({
      organizationId,
      propertyId,
      options: {
        onSuccess: () => {
          toastr.success(
            'Success',
            'Applicant selected from waitlist successfully',
          );
          onSuccess(affordableWaitlistRow?.applicationId)();
        },
        onError: (e) => toastr.error('Error', e?.message ?? e),
      },
    });

  const ReactHookFormProps = useReactHookFormProps({
    defaultValues: {
      floorPlanId: null,
      preferredMoveInDate: moment.utc(new Date().toISOString().slice(0, 10)),
      reasons: [],
      note: '',
    },
    mode: 'onChange',
  });
  const { getValues, isValid, isDirty, watch } = ReactHookFormProps;
  const selectedReasons = watch('reasons');
  const requireNote =
    Array.isArray(selectedReasons) &&
    selectedReasons.includes(_.camelCase('Other - See Notes'));

  const canAct = useHasPermission(WAITLIST_ACTION_SCOPE);

  const actionsProps = [
    {
      children: cancel,
      onClick: handleClose,
    },
    {
      children: <FormattedMessage {...selectFromWaitlistMessages.confirm} />,
      submitButton: true,
      isSubmitting,
      disabled: !isValid || !isDirty,
      onClick: () => {
        const values = getValues();
        const payload = buildSelectFromWaitlistPayload({
          affordableWaitlistRow,
          floorPlanId: values.floorPlanId,
          preferredMoveInDate:
            values.preferredMoveInDate?.format?.('YYYY-MM-DD') ??
            values.preferredMoveInDate,
          reasons: values.reasons,
          description: values.note,
        });

        selectFromWaitlistMutate({ payload, affordableWaitlistId });
      },
    },
  ];

  const modalProps = {
    title: <FormattedMessage {...messages.selectFromWaitlist} />,
    ContentProps: { sx: { maxWidth: '680px' } },
    actionsProps,
    // The endpoint refuses this without the scope, so the footer says so
    // rather than letting the agent submit into a 403.
    ...(canAct
      ? {}
      : {
          actions: (
            <PermissionGatedActions
              actionsProps={actionsProps}
              tooltip={
                <FormattedMessage
                  {...waitlistActionMessages.permissionRequired}
                />
              }
            />
          ),
        }),
  };

  const formProps = {
    fields:
      rank !== 1
        ? [
            {
              variant: 'multiSelect',
              name: 'reasons',
              label: (
                <FormattedMessage {...selectFromWaitlistMessages.reason} />
              ),
              options: skipReasons.map((name) => ({
                value: _.camelCase(name),
                text: name,
              })),
              rules: { required: true },
              GridProps: { xs: 12 },
            },
            {
              variant: 'text',
              name: 'note',
              label: <FormattedMessage {...selectFromWaitlistMessages.notes} />,
              placeholder: 'Start Typing...',
              rules: {
                required: requireNote,
                validate: (note) => {
                  if (requireNote && !note) {
                    return intl.formatMessage(
                      selectFromWaitlistMessages.notesRequired,
                    );
                  }
                  if (note && note.trim().length > 255) {
                    return intl.formatMessage(
                      selectFromWaitlistMessages.limitReached255,
                    );
                  }
                  return true;
                },
              },
              InputProps: { multiline: true, minRows: 4, maxRows: 5 },
              GridProps: { xs: 12 },
            },
          ]
        : [],
  };

  return (
    <SelectFromWaitlistModal
      organizationId={organizationId}
      propertyId={propertyId}
      open={open}
      handleClose={handleClose}
      modalProps={modalProps}
      ReactHookFormProps={ReactHookFormProps}
      formProps={formProps}
      affordableWaitlistRow={affordableWaitlistRow}
      separateByBedroom={separateByBedroom}
      waitlistV2Programs={waitlistV2Programs}
    />
  );
};

export default SelectFromWaitlist;
